// AgentTemplate sub-entity client — restores the "Templates" Agent tab dropped in the
// 2026-08-19/20 AgentEditor.tsx refactor (2,347 lines -> 7 tabs). Targets the real live backend
// (BizFirst.Ai.AiAgent.Api.Base's BaseAgentTemplateController), verified by reading the
// controller and entity directly, not carried over from the old deleted frontend's assumptions.
//
// Route correction found during this pass: the task's own briefing said the base route was
// `/api/v1/ai-agent/templates` ("matches what the old deleted frontend assumed"). Reading
// BaseAgentTemplateController.cs directly shows `[Route("api/v1/ai-agent/agent-templates")]` —
// `/agent-templates`, not `/templates`. Trusting the live backend per this phase's instructions;
// documented in DevelopmentHistoryLog.md.
//
// This client uses the controller's *generic* CRUD actions (bare POST/PUT/DELETE, bound to
// InsertWebRequest/UpdateWebRequest/DeleteWebRequest against the real AgentTemplate entity
// fields) rather than its dedicated create-template/update-template/delete-template routes —
// those bind to CreateTemplateRequest/UpdateTemplateRequest DTOs that additionally accept a
// `Description` field the service layer silently never persists (confirmed by reading
// AgentTemplate.cs: no Description property exists on the entity at all). Using the generic
// routes means there's no such dead field to wire up. See types.ts's AgentTemplateDTO comment.

import { BaseAdminApiClient, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentTemplateDTO, AgentTemplateFormValues } from '../types';

export class AgentTemplateAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getTemplatesByAgent(agentId: number): Promise<AdminApiResult<AgentTemplateDTO[]>> {
    const result = await this.postQuery<AgentTemplateDTO[]>('/api/v1/ai-agent/agent-templates/by-agent-id', {
      AgentID: { ID: agentId },
      PageInfo: { PageNumber: 1, PageSize: 1000 },
    });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async createTemplate(agentId: number, template: AgentTemplateFormValues): Promise<AdminApiResult<AgentTemplateDTO>> {
    return this.post<AgentTemplateDTO>('/api/v1/ai-agent/agent-templates', { AgentID: agentId, ...template });
  }

  async updateTemplate(templateId: number, template: Partial<AgentTemplateFormValues>): Promise<AdminApiResult<AgentTemplateDTO>> {
    return this.put<AgentTemplateDTO>(`/api/v1/ai-agent/agent-templates/${templateId}`, {
      AgentTemplateID: templateId,
      ...template,
    });
  }

  async deleteTemplate(templateId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-agent/agent-templates/${templateId}`, { id: templateId });
  }
}
